import {
  Backdrop,
  Box,
  ButtonBase,
  CircularProgress,
  Modal as MuiModal,
  Typography,
} from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import CloseIcon from '@material-ui/icons/Close'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import FilterListIcon from '@material-ui/icons/FilterList'
import { IRouteDashboard, TRouteData } from 'interfaces/IDashboard'
import {
  describeRouteOverflowCause,
  describeRouteOverflowResolution,
  routeOverflowCauseSummary,
} from 'pages/Operation/Conference/routeOverflowPresentation'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IRouteCapacityDetail, routeService } from 'services/routeService'
import { mapTagColor } from 'utils/mapTagColor'

import { useStyles } from './styles'

interface IModalRoute {
  handleClose: () => void
  open: boolean
  data: IRouteDashboard
}

type RouteStage = 'separation' | 'checked' | 'loaded'

const HISTORY_PATH = '/operacao/conferencia/rotas-estouradas/historico'

const LOADED_STATUS = ['em carregamento', 'carregado']
const CHECKED_STATUS = ['separada', 'conferida']

const STAGES: Array<{ id: RouteStage; label: string }> = [
  { id: 'separation', label: 'Em separação' },
  { id: 'checked', label: 'Conferido' },
  { id: 'loaded', label: 'Carregado' },
]

const resolveTagColor = (value: string) =>
  /^#[0-9a-f]{3,8}$/i.test(value || '') ? value : mapTagColor(value)

const formatColorLabel = (value: string) => {
  if (!value) return 'Cor não definida'
  if (/^#[0-9a-f]{3,8}$/i.test(value)) return 'Cor de identificação'
  return `${value.charAt(0).toLocaleUpperCase('pt-BR')}${value.slice(1).toLocaleLowerCase('pt-BR')}`
}

const routeStage = (route: TRouteData): RouteStage => {
  const status = (route.status || '').toLocaleLowerCase('pt-BR')
  if (LOADED_STATUS.includes(status)) return 'loaded'
  if (CHECKED_STATUS.includes(status)) return 'checked'
  return 'separation'
}

const hasOverflow = (route: TRouteData) =>
  route.need_reduce || route.is_reduced || Boolean(route.incident_status)

export const ModalRoute = ({ data, open, handleClose }: IModalRoute) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const [stage, setStage] = useState<RouteStage>('separation')
  const [attentionOnly, setAttentionOnly] = useState(false)
  const [expandedRoute, setExpandedRoute] = useState<number | null>(null)
  const [details, setDetails] = useState<Record<number, IRouteCapacityDetail>>({})
  const [loadingRoutes, setLoadingRoutes] = useState<Record<number, boolean>>({})
  const [failedRoutes, setFailedRoutes] = useState<Record<number, boolean>>({})
  // Cada abertura do modal é uma sessão: respostas de sessões anteriores são descartadas.
  const sessionRef = useRef(0)
  // Última requisição em andamento por rota: respostas antigas da mesma rota são ignoradas.
  const requestRef = useRef<Record<number, number>>({})
  const requestCounter = useRef(0)

  const routes = [...(data.data_routes || [])].sort((left, right) => {
    if (left.need_reduce !== right.need_reduce) return left.need_reduce ? -1 : 1
    if (left.need_reduce && right.need_reduce) {
      const leftDate = left.incident_detected_at || ''
      const rightDate = right.incident_detected_at || ''
      if (leftDate !== rightDate) return leftDate.localeCompare(rightDate)
    }
    return left.route - right.route
  })

  const attentionCount = routes.filter(route => route.need_reduce).length
  // O dado é atualizado pelo polling do CardRoute enquanto o modal está aberto;
  // o estado só é reiniciado na abertura, por isso a contagem vem de um ref.
  const attentionCountRef = useRef(attentionCount)
  attentionCountRef.current = attentionCount

  useEffect(() => {
    sessionRef.current += 1
    requestRef.current = {}
    if (!open) return
    // Havendo rota estourada o modal abre com o filtro "Só atenção" ligado.
    setAttentionOnly(attentionCountRef.current > 0)
    setStage('separation')
    setExpandedRoute(null)
    setDetails({})
    setFailedRoutes({})
    setLoadingRoutes({})
  }, [open])

  // Se o polling zerar as estouradas, o filtro é desligado de fato (e não só escondido),
  // para não religar sozinho quando uma rota voltar a estourar.
  useEffect(() => {
    if (attentionCount === 0) setAttentionOnly(false)
  }, [attentionCount])

  const filtering = attentionOnly && attentionCount > 0
  const countByStage = (id: RouteStage) => routes.filter(route => routeStage(route) === id).length
  const filteredRoutes = filtering
    ? routes.filter(route => route.need_reduce)
    : routes.filter(route => routeStage(route) === stage)

  const selectStage = (id: RouteStage) => {
    setAttentionOnly(false)
    setStage(id)
  }

  const loadIncident = async (route: number) => {
    const session = sessionRef.current
    requestCounter.current += 1
    const requestId = requestCounter.current
    requestRef.current[route] = requestId
    const isCurrent = () =>
      session === sessionRef.current && requestRef.current[route] === requestId

    setLoadingRoutes(current => ({ ...current, [route]: true }))
    setFailedRoutes(current => ({ ...current, [route]: false }))
    try {
      const detail = await routeService.getRouteCapacity(route)
      if (!isCurrent()) return
      setDetails(current => ({ ...current, [route]: detail }))
    } catch {
      if (!isCurrent()) return
      setFailedRoutes(current => ({ ...current, [route]: true }))
    } finally {
      if (isCurrent()) {
        delete requestRef.current[route]
        setLoadingRoutes(current => ({ ...current, [route]: false }))
      }
    }
  }

  const toggleRoute = (route: TRouteData) => {
    if (!hasOverflow(route)) return
    if (expandedRoute === route.route) {
      setExpandedRoute(null)
      return
    }
    setExpandedRoute(route.route)
    if (!details[route.route] && !requestRef.current[route.route]) loadIncident(route.route)
  }

  const emptyMessage = filtering
    ? 'Nenhuma rota exige atenção'
    : {
        separation: 'Nenhuma rota em separação',
        checked: 'Nenhuma rota conferida',
        loaded: 'Nenhuma rota carregada',
      }[stage]

  const updatedAt = (data.updated_in || '').slice(0, 5)
  const summary = [`${data.total_routes_in_carambola || 0} rotas monitoradas`]
  if (updatedAt) summary.push(`atualizado às ${updatedAt}`)

  const renderDetails = (route: TRouteData) => {
    if (loadingRoutes[route.route]) {
      return (
        <Box className={classes.detailsLoading}>
          <CircularProgress size="1rem" color="inherit" />
          <span>Carregando o motivo do estouro…</span>
        </Box>
      )
    }

    if (failedRoutes[route.route]) {
      return (
        <Typography className={classes.detailsError}>
          Não foi possível carregar o motivo do estouro desta rota.
        </Typography>
      )
    }

    const incident = details[route.route]?.incident
    if (!incident) {
      return (
        <Typography className={classes.detailsText}>
          Não há incidente de capacidade registrado para esta rota.
        </Typography>
      )
    }

    const resolution = describeRouteOverflowResolution(incident)
    const showResolution = incident.status !== 'pending'
    const isResolved = incident.status === 'resolved' && Boolean(incident.detected_at)

    return (
      <>
        <Typography className={classes.detailsTitle}>
          {routeOverflowCauseSummary(incident.cause_type)}
        </Typography>
        <Typography className={classes.detailsText}>
          {describeRouteOverflowCause(incident, {
            boxesCount: route.boxes_count,
            capacityVehicle: route.capacity_vehicle,
          })}
        </Typography>
        {showResolution && (
          <Typography className={isResolved ? classes.detailsResolution : classes.detailsText}>
            {[isResolved ? 'Regularizada' : null, resolution.title, resolution.metadata]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}
        {showResolution && resolution.detail && (
          <Typography className={classes.detailsText}>{resolution.detail}</Typography>
        )}
      </>
    )
  }

  const renderStatus = (route: TRouteData) => {
    if (route.need_reduce) {
      return (
        <span className={`${classes.status} ${classes.statusDanger}`}>
          <span className={classes.desktopOnly}>Estourada</span>
          <span className={classes.mobileOnly}>Diminuir {route.excess_boxes || 0}</span>
        </span>
      )
    }
    if (route.is_reduced) {
      return (
        <span className={`${classes.status} ${classes.statusSuccess}`}>
          <CheckCircleIcon className={classes.statusIcon} />
          Reduzida
        </span>
      )
    }
    return <span className={`${classes.status} ${classes.statusNeutral}`}>Normal</span>
  }

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      className={classes.root}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box
        className={classes.paper}
        role="dialog"
        aria-labelledby="modal-route-title"
        tabIndex={-1}
      >
        <Box component="header" className={classes.header}>
          <ButtonBase
            className={`${classes.backButton} ${classes.mobileOnly}`}
            onClick={handleClose}
            aria-label="Voltar"
          >
            <ChevronLeftIcon className={classes.headerIcon} />
          </ButtonBase>
          <Box className={classes.headerText}>
            <Typography className={classes.subtitle}>{summary.join(' · ')}</Typography>
            <Typography id="modal-route-title" className={classes.title}>
              Capacidade das rotas
            </Typography>
          </Box>
          <ButtonBase
            className={`${classes.closeButton} ${classes.desktopOnly}`}
            onClick={handleClose}
            aria-label="Fechar"
          >
            <CloseIcon className={classes.headerIcon} />
          </ButtonBase>
        </Box>

        <Box className={classes.tabsWrapper}>
          {attentionCount > 0 ? (
            <ButtonBase
              className={`${classes.attentionFilter} ${filtering ? classes.attentionFilterOn : ''}`}
              onClick={() => setAttentionOnly(current => !current)}
              aria-pressed={filtering}
            >
              <FilterListIcon className={classes.attentionIcon} />
              Só atenção
              <span className={classes.attentionCount}>{attentionCount}</span>
            </ButtonBase>
          ) : (
            <span className={classes.attentionClear}>
              <CheckCircleIcon className={classes.attentionIcon} />0 em atenção
            </span>
          )}

          <Box className={classes.tabs} role="group" aria-label="Filtrar rotas por etapa">
            {STAGES.map(option => {
              const active = !filtering && stage === option.id
              return (
                <ButtonBase
                  key={option.id}
                  aria-pressed={active}
                  className={`${classes.tab} ${active ? classes.tabActive : ''}`}
                  onClick={() => selectStage(option.id)}
                >
                  {option.label}
                  <span className={classes.tabCount}>{countByStage(option.id)}</span>
                </ButtonBase>
              )
            })}
          </Box>
        </Box>

        <Box className={classes.body}>
          <Box className={classes.tableHead} aria-hidden="true">
            <span>Rota</span>
            <span>Atual / limite</span>
            <span className={classes.alignRight}>Diminuir</span>
            <span className={classes.alignRight}>Status</span>
          </Box>

          <Box className={classes.routeList} role="list" aria-live="polite">
            {filteredRoutes.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography className={classes.emptyTitle}>{emptyMessage}</Typography>
                <Typography className={classes.emptyDescription}>
                  Os dados serão atualizados automaticamente.
                </Typography>
              </Box>
            ) : (
              filteredRoutes.map(route => {
                const capacity = route.capacity_vehicle
                const hasCapacity = capacity !== null && capacity !== undefined
                const expandable = hasOverflow(route)
                const expanded = expandedRoute === route.route
                const ChevronIcon = expanded ? ExpandLessIcon : ExpandMoreIcon

                return (
                  <Box
                    component="article"
                    role="listitem"
                    key={route.route}
                    className={`${classes.routeItem} ${
                      route.need_reduce ? classes.routeItemAttention : ''
                    } ${expandable ? classes.routeItemExpandable : ''}`}
                    onClick={expandable ? () => toggleRoute(route) : undefined}
                  >
                    <Box
                      className={classes.routeIdentity}
                      role={expandable ? 'button' : undefined}
                      tabIndex={expandable ? 0 : undefined}
                      aria-expanded={expandable ? expanded : undefined}
                      onKeyDown={
                        expandable
                          ? (event: KeyboardEvent<HTMLElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                toggleRoute(route)
                              }
                            }
                          : undefined
                      }
                    >
                      <ChevronIcon
                        className={`${classes.chevron} ${
                          route.need_reduce ? classes.chevronAttention : ''
                        } ${expandable ? '' : classes.chevronHidden}`}
                      />
                      <span
                        className={classes.colorDot}
                        style={{ backgroundColor: resolveTagColor(route.tagColor) }}
                        role="img"
                        aria-label={`Cor da rota: ${route.tagColor || 'não definida'}`}
                      />
                      <span
                        className={`${classes.routeNumber} ${
                          route.need_reduce ? classes.routeNumberAttention : ''
                        }`}
                      >
                        Rota {route.route}
                      </span>
                      <span className={classes.routeContext}>
                        · {formatColorLabel(route.tagColor)}
                      </span>
                    </Box>

                    <Typography className={classes.capacityValue}>
                      <strong>{route.boxes_count ?? 0}</strong>{' '}
                      <span>
                        / {hasCapacity ? capacity : '—'}
                        <span className={classes.mobileOnly}>&nbsp;caixas</span>
                      </span>
                    </Typography>

                    <Typography
                      className={`${classes.reduceValue} ${
                        route.need_reduce ? classes.reduceValueDanger : ''
                      }`}
                    >
                      {route.need_reduce ? route.excess_boxes || 0 : '—'}
                    </Typography>

                    <Box className={classes.statusCell}>{renderStatus(route)}</Box>

                    {expandable && expanded && (
                      <Box
                        className={`${classes.details} ${
                          route.need_reduce ? classes.detailsAttention : ''
                        }`}
                      >
                        <Typography className={classes.detailsLabel}>Motivo do estouro</Typography>
                        {renderDetails(route)}
                      </Box>
                    )}
                  </Box>
                )
              })
            )}
          </Box>

          <Box component="footer" className={classes.footer}>
            <Typography className={classes.footerNote}>
              A redução das caixas é feita pelo operador no galpão. Esta tela acompanha o status em
              tempo real.
            </Typography>
            <ButtonBase className={classes.historyButton} onClick={() => navigate(HISTORY_PATH)}>
              Ver histórico completo
              <ArrowForwardIcon className={classes.historyIcon} />
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    </MuiModal>
  )
}
